import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CreditCard, X, Loader2 } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

// Card-on-file entry for the portal's Saved Cards tab.
//
// Until now the only way a client could put a card on file was to complete a
// Square-hosted checkout from an invoice — so when Pay Now failed there was no
// second path (ticket #4, 2026-07-31). This mounts Square's Web Payments SDK
// card element directly and posts the resulting token to /payments/cards/add,
// which has existed server-side all along with no caller.
//
// NOTE the config field names: GET /payments/square-config returns app_id /
// location_id / environment. The staff-side PaymentAuthorizationModal reads
// config.application_id off its own endpoint — do not copy that shape here.

const SDK_URL = {
  production: 'https://web.squarecdn.com/v1/square.js',
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
};

// One <script> per page load; a second injection throws "Square is already defined".
let sdkPromise = null;
const loadSquareSdk = (environment) => {
  if (window.Square) return Promise.resolve(window.Square);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL[environment] || SDK_URL.sandbox;
    script.async = true;
    script.onload = () => (window.Square ? resolve(window.Square) : reject(new Error('sdk-absent')));
    script.onerror = () => { sdkPromise = null; reject(new Error('sdk-blocked')); };
    document.head.appendChild(script);
  });
  return sdkPromise;
};

export default function PortalAddCardModal({ isOpen, onClose, onAdded }) {
  const [status, setStatus] = useState('loading'); // loading | ready | submitting | error
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  // 2026-10-20 autopay: the client checks it themselves (a pre-checked box is no authorization)
  const [autopay, setAutopay] = useState(false);
  const cardRef = useRef(null);
  const attachedRef = useRef(false);

  const teardown = useCallback(async () => {
    attachedRef.current = false;
    const card = cardRef.current;
    cardRef.current = null;
    if (card) { try { await card.destroy(); } catch (e) { /* already gone */ } }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const config = await clientPortalService.getSquareConfig();
        if (!config?.app_id || !config?.location_id) throw new Error('unconfigured');

        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;

        const payments = Square.payments(config.app_id, config.location_id);
        const card = await payments.card();
        if (cancelled) { try { await card.destroy(); } catch (e) {} return; }

        // StrictMode runs effects twice in dev; attach exactly once.
        if (attachedRef.current) { try { await card.destroy(); } catch (e) {} return; }
        attachedRef.current = true;
        cardRef.current = card;
        await card.attach('#portal-card-container');
        if (cancelled) return;
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setError(
          e?.message === 'unconfigured'
            ? 'Card payments are not set up yet. Please contact the firm.'
            : e?.message === 'sdk-blocked'
              ? 'The secure card form could not load. If you use a content blocker, disable it for this site and try again.'
              : 'The card form could not be opened. Please try again.'
        );
      }
    })();

    return () => { cancelled = true; teardown(); };
  }, [isOpen, teardown]);

  const close = () => {
    if (status === 'submitting') return;
    setCardholderName('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!cardRef.current || status !== 'ready') return;
    setStatus('submitting');
    setError(null);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        // Square reports per-field problems here (declined, bad CVV, expired).
        const detail = (result.errors || []).map((x) => x.message).filter(Boolean).join(' ');
        setError(detail || 'Please check the card details and try again.');
        setStatus('ready');
        return;
      }
      const saved = await clientPortalService.addCard(result.token, cardholderName.trim() || null, autopay);
      setCardholderName('');
      setAutopay(false);
      onAdded?.(saved);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'That card could not be saved. Please try again.');
      setStatus('ready');
    }
  };

  if (!isOpen) return null;

  const busy = status === 'submitting';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="w-full sm:max-w-md bg-virgo-navy-800 border border-virgo-teal-700/40 rounded-t-2xl sm:rounded-2xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <CreditCard className="text-cyan-400" size={20} />
            <h2 className="text-white font-semibold">Add a card</h2>
          </div>
          <button onClick={close} disabled={busy} className="text-virgo-teal-400 hover:text-white p-1 disabled:opacity-40">
            <X size={20} />
          </button>
        </div>

        <label className="block text-virgo-teal-400 text-sm mb-1.5" htmlFor="portal-cardholder-name">
          Name on card <span className="text-virgo-teal-600">(optional)</span>
        </label>
        <input
          id="portal-cardholder-name"
          type="text"
          autoComplete="cc-name"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          disabled={busy}
          className="w-full mb-4 px-3 py-2.5 bg-virgo-navy-900 border border-virgo-teal-700/40 rounded-lg text-white text-base placeholder-virgo-teal-600 focus:outline-none focus:border-cyan-500/60 disabled:opacity-50"
          placeholder="Name as it appears on the card"
        />

        {/* Square injects its own iframe here; keep the node mounted while open. */}
        <div id="portal-card-container" className="min-h-[90px]" />

        <label className="mt-4 flex items-start gap-3 text-sm text-virgo-teal-300 cursor-pointer">
          <input
            type="checkbox"
            checked={autopay}
            onChange={(e) => setAutopay(e.target.checked)}
            disabled={busy}
            className="mt-1 h-4 w-4 shrink-0 accent-cyan-500"
          />
          <span>
            <span className="block text-white font-medium mb-1">Pay automatically</span>
            I authorize Astraea Law, P.A. to charge this card automatically for the payments due under my engagement letter, on each due date and in the amount the letter states, until those payments are complete. I can cancel this authorization at any time by telling the firm in writing; I will then receive a payment link for each remaining payment instead.
          </span>
        </label>

        {status === 'loading' && (
          <div className="flex items-center gap-2 text-virgo-teal-500 text-sm py-3">
            <Loader2 className="animate-spin" size={16} /> Loading secure card form…
          </div>
        )}

        {error && (
          <p className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <button
          onClick={submit}
          disabled={status !== 'ready'}
          className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg font-medium hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy && <Loader2 className="animate-spin" size={16} />}
          {busy ? 'Saving…' : 'Save card'}
        </button>

        <p className="mt-3 text-virgo-teal-600 text-xs text-center">
          Card details go straight to Square. The firm never sees or stores your full card number.
        </p>
      </div>
    </div>
  );
}
